// Culture and sensitivity records (docs/plans/phase3-lab-qc-and-microbiology.md §1.2, §4).
//
// What the lab enters, checked for shape only: no breakpoints, no expert rules, no
// interpretation. S, I and R are stored as the lab entered them (plan §1.2.6). The outcome
// list and the wording are provisional (plan Q13, Q15, TODO(clinical-verify)).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import { parseLabNumeric, type LabComparator } from './values.ts';

export const CULTURE_OUTCOMES = ['pending', 'no_growth', 'growth'] as const;
export type CultureOutcome = (typeof CULTURE_OUTCOMES)[number];

export const CULTURE_OUTCOME_WORDS: Record<CultureOutcome, string> = {
  pending: 'Pending',
  no_growth: 'No growth',
  growth: 'Growth',
};

export const SUSCEPTIBILITY_RESULTS = ['S', 'I', 'R'] as const;
export type SusceptibilityResult = (typeof SUSCEPTIBILITY_RESULTS)[number];

/** For screen readers and the printed key (plan Q15). */
export const SUSCEPTIBILITY_WORDS: Record<SusceptibilityResult, string> = {
  S: 'Susceptible',
  I: 'Intermediate',
  R: 'Resistant',
};

/** MIC values take up to this many decimals (0.0625 mg/L and similar). */
export const MIC_DECIMALS = 4;
/** A zone diameter is a whole number of millimetres, at most this. */
export const MAX_ZONE_MM = 999;
export const MAX_ISOLATES = 10;
export const MAX_ANTIBIOTICS_PER_ISOLATE = 40;

export interface CultureSusceptibilityInput {
  readonly antibioticId: string;
  readonly result: SusceptibilityResult;
  /** As typed, e.g. "≤0.25" or "32"; null when not entered. */
  readonly mic: string | null;
  readonly zoneMm: number | null;
}

export interface CultureIsolateInput {
  readonly isolateNumber: number;
  readonly organismId: string;
  readonly susceptibilities: readonly CultureSusceptibilityInput[];
}

export interface CultureInput {
  readonly outcome: CultureOutcome;
  readonly isolates: readonly CultureIsolateInput[];
}

export type CultureErrorCode =
  | 'culture.outcome_pending'
  | 'culture.growth_needs_isolate'
  | 'culture.no_growth_with_isolates'
  | 'culture.isolate_number'
  | 'culture.duplicate_isolate'
  | 'culture.duplicate_antibiotic'
  | 'culture.too_many'
  | 'culture.mic_invalid'
  | 'culture.zone_invalid';

export const CULTURE_ERRORS: Record<CultureErrorCode, string> = {
  'culture.outcome_pending': 'Choose No growth or Growth before submitting',
  'culture.growth_needs_isolate': 'Add the organism grown before submitting',
  'culture.no_growth_with_isolates': 'Remove the isolates, or change the outcome to Growth',
  'culture.isolate_number': 'Isolates are numbered from 1',
  'culture.duplicate_isolate': 'Each isolate number can be used once',
  'culture.duplicate_antibiotic': 'Each antibiotic can be entered once per isolate',
  'culture.too_many': 'Too many isolates or antibiotics in one culture',
  'culture.mic_invalid': 'Enter the MIC as a number, with < or > if needed',
  'culture.zone_invalid': 'Enter the zone as whole millimetres',
};

export interface ParsedMic {
  /** × 10^MIC_DECIMALS. */
  readonly scaled: number;
  readonly comparator: LabComparator | null;
}

/** An MIC as typed: "0.25", "≤0.25", ">32". Never negative. */
export function parseMic(text: string): Result<ParsedMic, 'culture.mic_invalid'> {
  const parsed = parseLabNumeric(text, { decimals: MIC_DECIMALS, allowsNegative: false });
  if (!parsed.ok) return err('culture.mic_invalid');
  return ok({ scaled: parsed.value.scaled, comparator: parsed.value.comparator });
}

/**
 * Checks a culture's shape. `save` allows any outcome (work in progress, and preliminary
 * reports); `final` (submission) needs No growth with no isolates, or Growth with at least
 * one.
 */
export function checkCulture(
  culture: CultureInput,
  purpose: 'save' | 'final',
): Result<CultureInput, CultureErrorCode> {
  if (culture.isolates.length > MAX_ISOLATES) return err('culture.too_many');
  const numbers = new Set<number>();
  for (const isolate of culture.isolates) {
    if (!Number.isInteger(isolate.isolateNumber) || isolate.isolateNumber < 1)
      return err('culture.isolate_number');
    if (isolate.isolateNumber > MAX_ISOLATES) return err('culture.isolate_number');
    if (numbers.has(isolate.isolateNumber)) return err('culture.duplicate_isolate');
    numbers.add(isolate.isolateNumber);
    if (isolate.susceptibilities.length > MAX_ANTIBIOTICS_PER_ISOLATE)
      return err('culture.too_many');
    const antibiotics = new Set<string>();
    for (const s of isolate.susceptibilities) {
      if (antibiotics.has(s.antibioticId)) return err('culture.duplicate_antibiotic');
      antibiotics.add(s.antibioticId);
      if (s.mic !== null && !parseMic(s.mic).ok) return err('culture.mic_invalid');
      if (
        s.zoneMm !== null &&
        (!Number.isInteger(s.zoneMm) || s.zoneMm < 0 || s.zoneMm > MAX_ZONE_MM)
      )
        return err('culture.zone_invalid');
    }
  }
  if (culture.outcome === 'no_growth' && culture.isolates.length > 0)
    return err('culture.no_growth_with_isolates');
  if (purpose === 'final') {
    if (culture.outcome === 'pending') return err('culture.outcome_pending');
    if (culture.outcome === 'growth' && culture.isolates.length === 0)
      return err('culture.growth_needs_isolate');
  }
  return ok(culture);
}

/** "≤0.25", ">32", "0.0625": an MIC as stored, for display. */
export function formatMic(mic: ParsedMic): string {
  const symbols: Record<LabComparator, string> = { lt: '<', le: '≤', gt: '>', ge: '≥' };
  const digits = String(mic.scaled).padStart(MIC_DECIMALS + 1, '0');
  const whole = digits.slice(0, -MIC_DECIMALS);
  const fraction = digits.slice(-MIC_DECIMALS).replace(/0+$/, '');
  const number = fraction === '' ? whole : `${whole}.${fraction}`;
  return mic.comparator === null ? number : `${symbols[mic.comparator]}${number}`;
}
